import type { ApolloClient, TypedDocumentNode } from "@apollo/client";
import type { TokenManager } from "./tokenManager";
import type { User } from "./types";
import {
  EmailNotConfirmedError,
  EmailAlreadyExistsError,
  CaptchaVerificationRequiredError,
  CaptchaVerificationFailedError,
} from "./errors";
import { AuthErrorKey, mapAuthServerError } from "./mapAuthError";
import type { AuthErrorBody } from "./mapAuthError";

export {
  EmailNotConfirmedError,
  EmailAlreadyExistsError,
  CaptchaVerificationRequiredError,
  CaptchaVerificationFailedError,
};

const LOGIN_SCOPE =
  "offline_access openid profile email phone roles api backup belege";

export interface LoginResponse {
  user: User;
  token: string;
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  id_token?: string;
}

/** Decodes a JWT payload without any network call. */
function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return {};
  }
}

export interface CreateAuthClientConfig<
  TUserQueryData extends { user?: unknown },
> {
  authEndpoint: string;
  /** Apollo client used to run `getUserDocument` (same server/schema for both apps). */
  client: ApolloClient;
  getUserDocument: TypedDocumentNode<TUserQueryData, Record<string, never>>;
  tokenManager: TokenManager;
  /** Maps the app's `GetUser` query result (`data.user`) into the unified `User` shape. */
  mapUser: (userData: NonNullable<TUserQueryData["user"]>) => User;
  /**
   * Builds a `User` straight from the JWT when the `GetUser` query fails or returns no data.
   * Optional: only set this if the app wants to tolerate a `GetUser` failure instead of throwing.
   */
  fallbackUserFromToken?: (token: string) => User;
}

/** Config-driven auth client shared by backoffice and reseller (same OpenIddict auth server). */
export function createAuthClient<
  TUserQueryData extends { user?: unknown } = { user?: unknown },
>(config: CreateAuthClientConfig<TUserQueryData>) {
  const {
    authEndpoint,
    client,
    getUserDocument,
    tokenManager,
    mapUser,
    fallbackUserFromToken,
  } = config;

  async function fetchAndMapUser(accessToken: string): Promise<User> {
    try {
      const result = await client.query({ query: getUserDocument });
      const userData = result.data?.user;
      if (userData) {
        return mapUser(userData as NonNullable<TUserQueryData["user"]>);
      }
      if (result.error) {
        console.warn("[Auth] GetUser GraphQL error:", result.error);
      }
    } catch (error) {
      console.warn("[Auth] GetUser failed:", error);
      if (!fallbackUserFromToken) {
        throw error;
      }
    }

    if (fallbackUserFromToken) {
      return fallbackUserFromToken(accessToken);
    }
    throw new Error(AuthErrorKey.FetchUserDataFailed);
  }

  /**
   * Calls GetUser (triggering server-side tenant provisioning) and refreshes the token
   * only if TenantId was absent — i.e. a new user whose claim was just added server-side.
   * Existing users skip the refresh entirely.
   */
  async function completeLogin(accessToken: string): Promise<LoginResponse> {
    const user = await fetchAndMapUser(accessToken);

    if (!decodeJwtPayload(accessToken).TenantId) {
      await tokenManager.refreshToken();
    }

    return { user, token: tokenManager.getAccessTokenSync() ?? accessToken };
  }

  async function login(
    email: string,
    password: string,
  ): Promise<LoginResponse> {
    try {
      const tokenParams = new URLSearchParams({
        username: email,
        password,
        grant_type: "password",
        scope: LOGIN_SCOPE,
      });

      const tokenResponse = await fetch(`${authEndpoint}/connect/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
        },
        body: tokenParams.toString(),
      });

      if (!tokenResponse.ok) {
        let body: AuthErrorBody | undefined;
        try {
          body = (await tokenResponse.json()) as AuthErrorBody;
        } catch (parseError) {
          throw new Error(
            `HTTP ${tokenResponse.status}: ${tokenResponse.statusText}`,
            { cause: parseError },
          );
        }

        const key = mapAuthServerError(body, AuthErrorKey.LoginFailed);
        if (key === AuthErrorKey.EmailNotConfirmed) {
          throw new EmailNotConfirmedError(AuthErrorKey.EmailNotConfirmed);
        }
        throw new Error(key);
      }

      const tokenData: TokenResponse = await tokenResponse.json();
      tokenManager.setTokens(
        tokenData.access_token,
        tokenData.refresh_token,
        tokenData.expires_in,
        tokenData.id_token,
      );

      return completeLogin(tokenData.access_token);
    } catch (error) {
      console.error("Auth error:", error);
      throw error;
    }
  }

  async function verifyToken(): Promise<User> {
    const token = tokenManager.getAccessTokenSync();
    if (!token) throw new Error("Nicht angemeldet");
    return fetchAndMapUser(token);
  }

  async function refreshToken(): Promise<string> {
    try {
      return await tokenManager.refreshToken();
    } catch (error) {
      console.error("Token refresh error:", error);
      logout();
      throw error;
    }
  }

  async function refreshTokenFromUrl(
    refreshToken: string,
  ): Promise<LoginResponse> {
    try {
      const accessToken =
        await tokenManager.refreshTokenWithExternal(refreshToken);
      return completeLogin(accessToken);
    } catch (error) {
      console.error("Token refresh from URL error:", error);
      throw error;
    }
  }

  async function loginWithAccessToken(
    accessToken: string,
    refreshToken?: string,
    expiresIn?: number,
    idToken?: string,
  ): Promise<LoginResponse> {
    try {
      const refreshTokenToStore =
        refreshToken || tokenManager.getRefreshToken() || accessToken;
      const defaultExpiresIn = expiresIn || 3600;
      tokenManager.setTokens(
        accessToken,
        refreshTokenToStore,
        defaultExpiresIn,
        idToken,
      );

      const user = await fetchAndMapUser(accessToken);
      return { user, token: accessToken };
    } catch (error) {
      console.error("Login with access token error:", error);
      throw error;
    }
  }

  async function resendConfirmationEmail(
    email: string,
    frontendUrl?: string,
  ): Promise<void> {
    const response = await fetch(`${authEndpoint}/Account/resendConfirm`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        Email: email,
        FrontendUrl: frontendUrl ?? window.location.origin,
      }),
    });

    if (!response.ok) {
      throw new Error("ResendConfirmationFailed");
    }
  }

  async function register(
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    confirmPassword: string,
    turnstileToken?: string,
  ): Promise<void> {
    try {
      const response = await fetch(`${authEndpoint}/Account/Register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          FirstName: firstName,
          LastName: lastName,
          Email: email,
          Password: password,
          ConfirmPassword: confirmPassword,
          FrontendUrl: window.location.origin,
          TurnstileToken: turnstileToken,
        }),
      });

      if (!response.ok) {
        let body: AuthErrorBody | undefined;
        try {
          body = (await response.json()) as AuthErrorBody;
        } catch (parseError) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`, {
            cause: parseError,
          });
        }

        const key = mapAuthServerError(body, AuthErrorKey.RegistrationFailed);
        if (key === AuthErrorKey.EmailAlreadyExists) {
          throw new EmailAlreadyExistsError(AuthErrorKey.EmailAlreadyExists);
        }
        if (key === AuthErrorKey.CaptchaRequired) {
          throw new CaptchaVerificationRequiredError(
            AuthErrorKey.CaptchaRequired,
          );
        }
        if (key === AuthErrorKey.CaptchaFailed) {
          throw new CaptchaVerificationFailedError(AuthErrorKey.CaptchaFailed);
        }
        throw new Error(key);
      }
    } catch (error) {
      console.error("Registration error:", error);
      throw error;
    }
  }

  function logout(): void {
    tokenManager.clearTokens();
  }

  async function forgetPassword(
    email: string,
    frontendUrl?: string,
  ): Promise<void> {
    const response = await fetch(`${authEndpoint}/Account/Forget`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        email,
        FrontendUrl: frontendUrl ?? window.location.origin,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error_description ||
          errorData.error ||
          `Failed to send password reset email: ${response.statusText}`,
      );
    }
  }

  async function resetPassword(
    email: string,
    password: string,
    token: string,
  ): Promise<void> {
    const response = await fetch(`${authEndpoint}/Account/Reset`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        email,
        password,
        ConfirmPassword: password,
        token,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error_description ||
          errorData.error ||
          `Password reset failed: ${response.statusText}`,
      );
    }
  }

  return {
    login,
    verifyToken,
    refreshToken,
    refreshTokenFromUrl,
    loginWithAccessToken,
    resendConfirmationEmail,
    register,
    forgetPassword,
    resetPassword,
    logout,
  };
}

export type AuthClient = ReturnType<typeof createAuthClient>;
